import { CSRF_HEADER_NAME } from '@wingseat/shared';
import type { ZodSchema } from 'zod';
import { ensureCsrfInitialized, getCsrfToken } from './csrf';

/**
 * API-Base. Bei leerem Wert (Default) werden Requests relativ an `/api/<path>`
 * gesendet — das spielt mit dem Vite-Proxy (lokal) und einem öffentlich
 * erreichbaren Reverse-Proxy/Tunnel (mobile/Production) zusammen.
 *
 * Für Setups mit getrenntem API-Host kann `VITE_API_BASE_URL=https://api.example.com`
 * gesetzt werden — dann werden Requests absolut gesendet (CORS+Credentials nötig).
 */
const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions<TResponse> {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  schema?: ZodSchema<TResponse>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions<unknown>['query']): string {
  let target: string;
  if (path.startsWith('http')) {
    target = path;
  } else if (API_BASE_URL) {
    target = `${API_BASE_URL}${path}`;
  } else {
    // Relativ → Vite-Proxy / Reverse-Proxy hängt /api an localhost:3000 weiter
    target = `/api${path}`;
  }
  const base =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = target.startsWith('http') ? new URL(target) : new URL(target, base);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function sendRequest<TResponse>(
  path: string,
  options: RequestOptions<TResponse>,
  attempt: number,
): Promise<TResponse> {
  const method: Method = options.method ?? 'GET';
  const isMutation = method !== 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers ?? {}),
  };

  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (isMutation) {
    await ensureCsrfInitialized().catch(() => undefined);
    const csrfToken = getCsrfToken();
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  const res = await fetch(buildUrl(path, options.query), {
    method,
    credentials: 'include',
    headers,
    signal: options.signal,
    body:
      options.body === undefined
        ? undefined
        : options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body),
  });

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    // Auto-Retry bei abgelaufenem/ungültigem CSRF-Token: Cookie + Session
    // neu holen, dann genau EINMAL erneut versuchen.
    const isCsrfError =
      isMutation &&
      res.status === 403 &&
      payload &&
      typeof payload === 'object' &&
      (payload as { error?: unknown }).error === 'EBADCSRFTOKEN';
    if (isCsrfError && attempt === 0) {
      await ensureCsrfInitialized({ force: true }).catch(() => undefined);
      return sendRequest<TResponse>(path, options, attempt + 1);
    }
    const message =
      isJson && payload && typeof payload === 'object' && 'message' in payload
        ? String((payload as { message?: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, payload, message);
  }

  if (options.schema) return options.schema.parse(payload);
  return payload as TResponse;
}

export function apiRequest<TResponse = unknown>(
  path: string,
  options: RequestOptions<TResponse> = {},
): Promise<TResponse> {
  return sendRequest<TResponse>(path, options, 0);
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions<T>, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions<T>, 'method'>) =>
    apiRequest<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions<T>, 'method'>) =>
    apiRequest<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions<T>, 'method'>) =>
    apiRequest<T>(path, { ...opts, method: 'DELETE', body }),
};
